import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import type { Library } from "@/api/admin-api"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Spinner } from "@/components/ui/spinner"

import { LibraryEditorForm } from "./library-editor/library-editor-form"
import {
  EMPTY_EDITOR_VALUES,
  type LibraryEditorValues,
} from "./library-editor/library-editor-model"
import { useLibraryEditor } from "./library-editor/use-library-editor"

type AddLibraryDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (values: LibraryEditorValues) => Promise<void>
  isPending: boolean
  embeddingModels?: Array<{ id: string; displayName: string }>
  /** Existing libraries (for optional copy-auth); SharePoint-family entries with auth are listed */
  existingLibraries?: Library[]
}

function freshValues(): LibraryEditorValues {
  return { ...EMPTY_EDITOR_VALUES, source_config: { ...EMPTY_EDITOR_VALUES.source_config } }
}

export function AddLibraryDialog({
  isOpen,
  onOpenChange,
  onSubmit,
  isPending,
  embeddingModels = [],
  existingLibraries = [],
}: AddLibraryDialogProps) {
  const { t } = useTranslation()

  // A new object per open so the editor resets to an empty form every time.
  const [initialValues, setInitialValues] = useState<LibraryEditorValues>(freshValues)
  useEffect(() => {
    if (!isOpen) setInitialValues(freshValues())
  }, [isOpen])

  const editor = useLibraryEditor({ mode: "create", initialValues, existingLibraries })

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!editor.validate()) return
    await onSubmit(editor.values)
    onOpenChange(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-[600px]">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{t("adminDashboard.libraries.addDialog.title", "Add Library")}</DialogTitle>
          <DialogDescription>
            {t(
              "adminDashboard.libraries.addDialog.description",
              "Configure a new library source for indexing. Libraries allow you to add custom data sources like SharePoint document libraries."
            )}
          </DialogDescription>
        </DialogHeader>
        <form className="flex min-h-0 flex-1 flex-col space-y-4" onSubmit={handleSubmit}>
          <div className="flex-1 overflow-y-auto pr-2">
            <LibraryEditorForm active={isOpen} editor={editor} embeddingModels={embeddingModels} />
          </div>
          <DialogFooter className="flex-shrink-0">
            <Button
              disabled={isPending}
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("adminDashboard.libraries.addDialog.cancel", "Cancel")}
            </Button>
            <Button disabled={isPending} type="submit">
              {isPending ? (
                <>
                  <Spinner />
                  {t("adminDashboard.libraries.addDialog.adding", "Adding...")}
                </>
              ) : (
                t("adminDashboard.libraries.addDialog.add", "Add Library")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
